import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import InputDate from "../../../components/InputDate";
import InputTextarea from "../../../components/InputTextarea";
import Toggle from "../../../components/Toggle";
import Icon from "../../../components/Icon";
import SelectCompany from "../../../components/selects/SelectCompany";
import SelectCustomer from "../../../components/selects/SelectCustomer";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import { getBillingCompanyLabel } from "../billingCompany";
import { flattenCategories, getCategoryLabel, isLeafCategory } from "../helpers";
import { usePaymentMethodOptions } from "../../../components/PaymentCondition/usePaymentOptions";

const RECURRENCE_MODES = [
    { label: 'Mensal, sem término', value: 'monthly' },
    { label: 'Anual, sem término', value: 'yearly' },
    { label: 'Personalizado', value: 'custom' },
];

const FREQUENCY_OPTIONS = [
    { label: 'dia(s)', value: 'day', momentUnit: 'days', adverb: 'diárias' },
    { label: 'semana(s)', value: 'week', momentUnit: 'weeks', adverb: 'semanais' },
    { label: 'mês(es)', value: 'month', momentUnit: 'months', adverb: 'mensais' },
    { label: 'ano(s)', value: 'year', momentUnit: 'years', adverb: 'anuais' },
];

const emptyAllocationRow = () => ({ key: Math.random().toString(36).slice(2), targetId: null, amount: 'R$ 0,00' });

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

const getFileExtension = name => {
    const parts = String(name ?? '').split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const isImageFile = name => IMAGE_EXTENSIONS.includes(getFileExtension(name));

const formatPercent = (amountCents, totalCents) => {
    if (!totalCents) return '0,00';
    return ((amountCents / totalCents) * 100).toFixed(2).replace('.', ',');
};

// Modal de lançamento (nova receita/despesa): dados principais à esquerda,
// abas de Anexos, Recorrência e Rateio à direita — ver rascunho aprovado.
export const ModalEntry = ({ show, close, entry, type, categories, costCenters, bankAccounts, billingCompanies = [], defaultBillingCompanyId = null, onSaved, level }) => {
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('attachments');
    const fileInputRef = useRef(null);

    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('R$ 0,00');
    const [dueDate, setDueDate] = useState('');
    const [competenceDate, setCompetenceDate] = useState('');
    const [referenceCode, setReferenceCode] = useState('');
    const [bankAccountId, setBankAccountId] = useState(null);
    const [billingCompanyId, setBillingCompanyId] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const { options: paymentMethodOptions } = usePaymentMethodOptions(entry?.payment_method_id);
    const [companyId, setCompanyId] = useState(null);
    const [customerId, setCustomerId] = useState(null);
    const [categoryId, setCategoryId] = useState(null);
    const [costCenterId, setCostCenterId] = useState(null);
    const [comments, setComments] = useState('');

    const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
    const [recurrenceMode, setRecurrenceMode] = useState('monthly');
    const [recurrenceInterval, setRecurrenceInterval] = useState('1');
    const [recurrenceFrequency, setRecurrenceFrequency] = useState('month');
    const [recurrenceOccurrences, setRecurrenceOccurrences] = useState('12');

    const [allocationEnabled, setAllocationEnabled] = useState(false);
    const [allocationDimension, setAllocationDimension] = useState('category');
    const [categoryRows, setCategoryRows] = useState([]);
    const [costCenterRows, setCostCenterRows] = useState([]);

    const [attachments, setAttachments] = useState([]);

    const isEdit = !!entry;
    const totalCents = convertMoenyToInt(amount) || 0;

    useEffect(() => {
        if (!show) return;

        setTab('attachments');
        setDescription(entry?.description ?? '');
        setAmount(convertIntToMoney(entry?.amount ?? 0, true));
        setDueDate(entry?.due_date ? entry.due_date.substring(0, 10) : '');
        setCompetenceDate(entry?.competence_date ? entry.competence_date.substring(0, 10) : '');
        setReferenceCode(entry?.reference_code ?? '');
        setBankAccountId(entry?.finance_bank_account_id ?? null);
        setBillingCompanyId(entry?.billing_company_id ?? defaultBillingCompanyId);
        setPaymentMethod(entry?.payment_method_id ?? null);
        setCompanyId(entry?.company_id ?? null);
        setCustomerId(entry?.customer_id ?? null);
        setCategoryId(entry?.finance_category_id ?? null);
        setCostCenterId(entry?.finance_cost_center_id ?? null);
        setComments(entry?.comments ?? '');

        setRecurrenceEnabled(false);
        setRecurrenceMode('monthly');
        setRecurrenceInterval('1');
        setRecurrenceFrequency('month');
        setRecurrenceOccurrences('12');

        const existingCategoryRows = (entry?.allocations ?? [])
            .filter(allocation => allocation.dimension === 'category')
            .map(allocation => ({ key: allocation.id, targetId: allocation.finance_category_id, amount: convertIntToMoney(allocation.amount, true) }));
        const existingCostCenterRows = (entry?.allocations ?? [])
            .filter(allocation => allocation.dimension === 'cost_center')
            .map(allocation => ({ key: allocation.id, targetId: allocation.finance_cost_center_id, amount: convertIntToMoney(allocation.amount, true) }));

        setCategoryRows(existingCategoryRows);
        setCostCenterRows(existingCostCenterRows);
        setAllocationEnabled(existingCategoryRows.length > 0 || existingCostCenterRows.length > 0);
        setAllocationDimension('category');

        setAttachments((entry?.attachments ?? []).map(attachment => ({
            key: attachment.id,
            id: attachment.id,
            kind: attachment.kind,
            description: attachment.description ?? '',
            url: attachment.url ?? '',
            pix_key: attachment.pix_key ?? '',
            name: attachment.name,
            file_url: attachment.file_url,
            previewUrl: attachment.kind === 'file' && isImageFile(attachment.name) ? attachment.file_url : null,
        })));
    }, [show, entry, defaultBillingCompanyId]);

    // O CNPJ vem junto do nome: tenant com duas empresas costuma ter nomes
    // parecidos, e é o CNPJ que a pessoa confere.
    const billingCompanyOptions = billingCompanies.map(company => ({
        label: (
            <span className={`billing-company-option`}>
                {getBillingCompanyLabel(company)}
                {company.cnpj && <small>{company.cnpj}</small>}
            </span>
        ),
        value: company.id,
    }));

    // Só analítica: a sintética soma as filhas no fluxo de caixa e aceitar
    // título nela furaria o subtotal (o backend também recusa). A categoria já
    // gravada continua na lista, senão some da tela de um título antigo.
    const categoryOptions = flattenCategories(categories)
        .filter(category => isLeafCategory(category) || category.id === categoryId)
        .map(category => ({ label: getCategoryLabel(category), value: category.id }));
    const costCenterOptions = costCenters.map(costCenter => ({ label: costCenter.name, value: costCenter.id }));
    const accountOptions = bankAccounts.map(account => ({ label: account.name, value: account.id }));

    const activeRows = allocationDimension === 'category' ? categoryRows : costCenterRows;
    const setActiveRows = allocationDimension === 'category' ? setCategoryRows : setCostCenterRows;
    const activeOptions = allocationDimension === 'category' ? categoryOptions : costCenterOptions;

    const rowsSum = rows => rows.reduce((sum, row) => sum + (convertMoenyToInt(row.amount) || 0), 0);
    const categoryRemaining = totalCents - rowsSum(categoryRows);
    const costCenterRemaining = totalCents - rowsSum(costCenterRows);
    const activeRemaining = allocationDimension === 'category' ? categoryRemaining : costCenterRemaining;

    const categoryAllocated = allocationEnabled && categoryRows.length > 0;
    const costCenterAllocated = allocationEnabled && costCenterRows.length > 0;

    const allocationsValid = (!categoryAllocated || categoryRemaining === 0)
        && (!costCenterAllocated || costCenterRemaining === 0);

    // Prévia da recorrência: datas geradas a partir do vencimento + total comprometido.
    const recurrencePreview = useMemo(() => {
        if (!recurrenceEnabled || !dueDate) return null;

        const custom = recurrenceMode === 'custom';
        const interval = custom ? (parseInt(recurrenceInterval, 10) || 1) : 1;
        const frequency = custom ? recurrenceFrequency : (recurrenceMode === 'yearly' ? 'year' : 'month');
        const occurrences = custom ? (parseInt(recurrenceOccurrences, 10) || 0) : null;
        const frequencyOption = FREQUENCY_OPTIONS.find(option => option.value === frequency);

        if (custom && occurrences < 2) return { error: 'Informe ao menos 2 ocorrências (incluindo esta)' };

        const previewDates = [];
        const previewCount = occurrences ? Math.min(occurrences, 3) : 3;
        for (let index = 0; index < previewCount; index++) {
            previewDates.push(moment(dueDate).add(interval * index, frequencyOption.momentUnit).format('DD/MM/YYYY'));
        }

        if (occurrences) {
            const lastDate = moment(dueDate).add(interval * (occurrences - 1), frequencyOption.momentUnit).format('DD/MM/YYYY');
            const label = interval === 1
                ? `${occurrences} ocorrências ${frequencyOption.adverb}`
                : `${occurrences} ocorrências a cada ${interval} ${frequencyOption.label}`;
            return {
                text: `${label}: ${previewDates.join(', ')}… última em ${lastDate}. Total: ${convertIntToMoney(totalCents * occurrences, true)}`
            };
        }

        const label = interval === 1 ? `Repetição ${frequencyOption.adverb.replace('árias', 'ária').replace('ais', 'al')}` : `A cada ${interval} ${frequencyOption.label}`;
        return { text: `${label}, sem término: ${previewDates.join(', ')}… (as próximas são geradas automaticamente)` };
    }, [recurrenceEnabled, recurrenceMode, recurrenceInterval, recurrenceFrequency, recurrenceOccurrences, dueDate, totalCents]);

    const addAllocationRow = () => {
        const row = emptyAllocationRow();
        if (activeRemaining > 0) row.amount = convertIntToMoney(activeRemaining, true);
        setActiveRows([...activeRows, row]);
    };

    const updateAllocationRow = (key, changes) => {
        setActiveRows(activeRows.map(row => row.key === key ? { ...row, ...changes } : row));
    };

    const removeAllocationRow = key => {
        setActiveRows(activeRows.filter(row => row.key !== key));
    };

    const handlePercentChange = (key, percentText) => {
        const percent = parseFloat(String(percentText).replace(',', '.'));
        if (Number.isNaN(percent)) return;
        updateAllocationRow(key, { amount: convertIntToMoney(Math.round(totalCents * (percent / 100)), true) });
    };

    // Anexos novos entram no topo da lista.
    const addFileAttachments = files => {
        const rows = Array.from(files).map(file => ({
            key: Math.random().toString(36).slice(2),
            kind: 'file',
            description: '',
            file,
            name: file.name,
            previewUrl: isImageFile(file.name) ? URL.createObjectURL(file) : null,
        }));
        setAttachments([...rows, ...attachments]);
    };

    const addLinkAttachment = () => {
        setAttachments([{
            key: Math.random().toString(36).slice(2),
            kind: 'link',
            description: '',
            url: '',
        }, ...attachments]);
    };

    const addPixAttachment = () => {
        setAttachments([{
            key: Math.random().toString(36).slice(2),
            kind: 'pix',
            description: '',
            pix_key: '',
        }, ...attachments]);
    };

    const copyPixKey = pixKey => {
        navigator.clipboard.writeText(pixKey).then(() => toast.success('Chave Pix copiada'));
    };

    const renderAttachmentTarget = attachment => {
        if (attachment.kind === 'file') {
            return attachment.file_url
                ? <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" title={attachment.name}>{attachment.name ?? 'arquivo'}</a>
                : <span title={attachment.name}>{attachment.name ?? 'arquivo'}</span>;
        }

        if (attachment.kind === 'pix') {
            return attachment.id
                ? <button type="button" className={`attachment-pix`} onClick={() => copyPixKey(attachment.pix_key)} title={`Copiar chave Pix`}>
                    <span>{attachment.pix_key}</span>
                    <Icon name={`copy`} size={14} />
                </button>
                : <Input placeholder={`CPF/CNPJ, e-mail, telefone ou aleatória`} value={attachment.pix_key} change={value => updateAttachment(attachment.key, { pix_key: value })} />;
        }

        return attachment.id
            ? <a href={attachment.url} target="_blank" rel="noopener noreferrer" title={attachment.url}>{attachment.url}</a>
            : <Input placeholder={`https://...`} value={attachment.url} change={value => updateAttachment(attachment.key, { url: value })} />;
    };

    const updateAttachment = (key, changes) => {
        setAttachments(attachments.map(attachment => attachment.key === key ? { ...attachment, ...changes } : attachment));
    };

    const removeAttachment = attachment => {
        if (attachment.id) {
            api.delete(`/finance-entry/attachment/${attachment.id}`).then(() => {
                setAttachments(current => current.filter(item => item.key !== attachment.key));
            }).catch(renderError);
            return;
        }
        if (attachment.previewUrl && attachment.file) {
            URL.revokeObjectURL(attachment.previewUrl);
        }
        setAttachments(attachments.filter(item => item.key !== attachment.key));
    };

    const buildEntryData = () => ({
        type,
        description,
        reference_code: referenceCode || null,
        amount: totalCents,
        due_date: dueDate,
        competence_date: competenceDate,
        billing_company_id: billingCompanyId || null,
        company_id: companyId || null,
        customer_id: customerId || null,
        finance_category_id: categoryAllocated ? null : (categoryId || null),
        finance_cost_center_id: costCenterAllocated ? null : (costCenterId || null),
        finance_bank_account_id: bankAccountId || null,
        payment_method_id: paymentMethod || null,
        comments: comments || null,
    });

    const buildAllocations = () => {
        if (!allocationEnabled) return null;

        const allocations = {};
        if (categoryRows.length > 0) {
            allocations.category = categoryRows.map(row => ({
                finance_category_id: row.targetId,
                amount: convertMoenyToInt(row.amount),
            }));
        }
        if (costCenterRows.length > 0) {
            allocations.cost_center = costCenterRows.map(row => ({
                finance_cost_center_id: row.targetId,
                amount: convertMoenyToInt(row.amount),
            }));
        }

        return Object.keys(allocations).length > 0 ? allocations : null;
    };

    const buildRecurrence = () => {
        if (!recurrenceEnabled || isEdit) return null;
        if (recurrenceMode === 'monthly') return { interval: 1, frequency: 'month' };
        if (recurrenceMode === 'yearly') return { interval: 1, frequency: 'year' };
        return {
            interval: parseInt(recurrenceInterval, 10) || 1,
            frequency: recurrenceFrequency,
            occurrences: parseInt(recurrenceOccurrences, 10) || null,
        };
    };

    const appendObjectToFormData = (formData, value, prefix) => {
        if (value === null || value === undefined) return;
        if (value instanceof File) {
            formData.append(prefix, value);
        } else if (Array.isArray(value)) {
            value.forEach((item, index) => appendObjectToFormData(formData, item, `${prefix}[${index}]`));
        } else if (typeof value === 'object') {
            Object.entries(value).forEach(([itemKey, itemValue]) => appendObjectToFormData(formData, itemValue, prefix ? `${prefix}[${itemKey}]` : itemKey));
        } else {
            formData.append(prefix, value);
        }
    };

    // A competência é o mês em que o DRE vai reconhecer o título, e quase
    // sempre é o do vencimento — mas quem digitou pode corrigir, e é por isso
    // que o vencimento só a preenche enquanto ela estiver vazia.
    const handleDueDate = date => {
        setDueDate(date);

        if (!competenceDate) {
            setCompetenceDate(date);
        }
    };

    const canSave = description.trim() !== '' && totalCents > 0 && dueDate !== '' && competenceDate !== '' && allocationsValid
        && !recurrencePreview?.error
        && !attachments.some(attachment => !attachment.id && (
            (attachment.kind === 'link' && attachment.url.trim() === '')
            || (attachment.kind === 'pix' && attachment.pix_key.trim() === '')
        ))
        && !(allocationEnabled && [...categoryRows, ...costCenterRows].some(row => !row.targetId));

    const save = () => {
        setSaving(true);

        if (!isEdit) {
            const payload = {
                ...buildEntryData(),
                allocations: buildAllocations(),
                recurrence: buildRecurrence(),
                attachments: attachments.map(attachment => ({
                    kind: attachment.kind,
                    description: attachment.description || null,
                    url: attachment.kind === 'link' ? attachment.url : null,
                    pix_key: attachment.kind === 'pix' ? attachment.pix_key : null,
                    file: attachment.file ?? null,
                })),
            };

            const hasFiles = attachments.some(attachment => attachment.file);
            const request = hasFiles
                ? (() => {
                    const formData = new FormData();
                    appendObjectToFormData(formData, payload, '');
                    return api.post(`/finance-entry`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                })()
                : api.post(`/finance-entry`, payload);

            request.then(() => {
                toast.success(type === 'receivable' ? 'Receita criada' : 'Despesa criada');
                close();
                onSaved();
            }).catch(renderError).then(() => setSaving(false));
            return;
        }

        api.put(`/finance-entry/${entry.id}`, {
            ...buildEntryData(),
            allocations: buildAllocations(),
        }).then(async () => {
            const pending = attachments.filter(attachment => !attachment.id);
            for (const attachment of pending) {
                const formData = new FormData();
                formData.append('kind', attachment.kind);
                if (attachment.description) formData.append('description', attachment.description);
                if (attachment.kind === 'link') formData.append('url', attachment.url);
                if (attachment.kind === 'pix') formData.append('pix_key', attachment.pix_key);
                if (attachment.file) formData.append('file', attachment.file);
                await api.post(`/finance-entry/${entry.id}/attachment`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }

            toast.success('Lançamento atualizado');
            close();
            onSaved();
        }).catch(renderError).then(() => setSaving(false));
    };

    const attachmentCount = attachments.length;
    const summaryParts = [];
    if (recurrenceEnabled && !recurrencePreview?.error) summaryParts.push(recurrenceMode === 'custom' ? 'recorrência personalizada' : (recurrenceMode === 'yearly' ? 'repetição anual' : 'repetição mensal'));
    if (entry?.recurrence) summaryParts.push(`ocorrência ${entry.recurrence_index}${entry.recurrence.occurrences ? `/${entry.recurrence.occurrences}` : ''} da série`);
    if (categoryAllocated) summaryParts.push(`rateado em ${categoryRows.length} ${categoryRows.length > 1 ? 'categorias' : 'categoria'}`);
    if (costCenterAllocated) summaryParts.push(`${costCenterRows.length} ${costCenterRows.length > 1 ? 'centros de custo' : 'centro de custo'}`);
    if (attachmentCount > 0) summaryParts.push(`${attachmentCount} ${attachmentCount > 1 ? 'anexos' : 'anexo'}`);

    const entityLabel = type === 'receivable' ? 'receita' : 'despesa';

    // Na despesa o tipo só vem sugerido: o fornecedor pode ser de produto,
    // de gravação ou de serviço sem tipo próprio.
    const createProps = {
        ...(type === 'receivable' ? { type: ['customer'] } : { defaultType: ['product'] }),
        level: (level ?? 0) + 1,
    };

    return (
        <Modal
            className={`finance-entry-modal`}
            level={level}
            size={`xlarge`}
            title={isEdit ? `Editar ${entityLabel}` : `Nova ${entityLabel}`}
            show={show}
            close={close}
            footer={
                <div className={`finance-modal-footer`}>
                    <span className={`footer-summary`}>{summaryParts.join(' · ')}</span>
                    <span className={`footer-actions`}>
                        <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                        <Button type={`primary`} size={`small`} text={isEdit ? 'Salvar' : `Salvar ${entityLabel}`} action={save} loading={saving} disabled={!canSave} />
                    </span>
                </div>
            }
        >
            <div className={`finance-entry-modal-body`}>
                <div className={`entry-main`}>
                    {billingCompanies.length > 1 &&
                        <div className="row">
                            <div className="col-12">
                                <InputSelect label={`Empresa de faturamento`} options={billingCompanyOptions} value={billingCompanyId} change={setBillingCompanyId} />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="col-12">
                            <Input label={`Descrição`} value={description} change={setDescription} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-4">
                            <Input label={`Valor`} value={amount} change={setAmount} mask={`convertToMoney`} />
                        </div>
                        <div className="col-4">
                            <InputDate label={`Vencimento`} format={`YYYY-MM-DD`} value={dueDate} change={handleDueDate} />
                        </div>
                        <div className="col-4">
                            <InputDate label={`Competência`} format={`YYYY-MM-DD`} value={competenceDate} change={setCompetenceDate} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <InputSelect menuInBody label={`Conta de pagamento`} options={accountOptions} value={bankAccountId} change={setBankAccountId} />
                        </div>
                        <div className="col-6">
                            <InputSelect
                                menuInBody
                                label={`Forma de pagamento`}
                                options={[{ label: 'Não informada', value: null }, ...paymentMethodOptions]}
                                value={paymentMethod}
                                change={setPaymentMethod}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <SelectCompany selectedCompany={companyId} setSelectedCompany={setCompanyId} label={type === 'payable' ? 'Fornecedor' : 'Empresa'} create={createProps} />
                        </div>
                        <div className="col-6">
                            <SelectCustomer selectedCustomer={customerId} setSelectedCustomer={setCustomerId} create={{ ...createProps, companies: companyId ? [companyId] : null }} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            {categoryAllocated
                                ? <div className={`allocation-placeholder`}><label>Categoria</label><span>Definida no rateio</span></div>
                                : <InputSelect menuInBody label={`Categoria`} options={categoryOptions} value={categoryId} change={setCategoryId} />
                            }
                        </div>
                        <div className="col-6">
                            {costCenterAllocated
                                ? <div className={`allocation-placeholder`}><label>Centro de custo</label><span>Definido no rateio</span></div>
                                : <InputSelect menuInBody label={`Centro de custo`} options={costCenterOptions} value={costCenterId} change={setCostCenterId} />
                            }
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <Input label={`Código de referência`} value={referenceCode} change={setReferenceCode} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <InputTextarea label={`Observações`} value={comments} change={setComments} rows={2} />
                        </div>
                    </div>
                </div>

                <div className={`entry-side`}>
                    <div className={`entry-tabs`}>
                        <button className={tab === 'attachments' ? 'active' : ''} onClick={() => setTab('attachments')}>
                            Anexos {attachmentCount > 0 && <span className={`counter`}>{attachmentCount}</span>}
                        </button>
                        <button className={tab === 'recurrence' ? 'active' : ''} onClick={() => setTab('recurrence')}>
                            Recorrência {(recurrenceEnabled || entry?.recurrence) && <span className={`indicator`} />}
                        </button>
                        <button className={tab === 'allocation' ? 'active' : ''} onClick={() => setTab('allocation')}>
                            Rateio {(categoryAllocated || costCenterAllocated) && <span className={`indicator`} />}
                        </button>
                    </div>

                    {tab === 'recurrence' &&
                        <div className={`entry-tab-content`}>
                            {entry?.recurrence
                                ? <div className={`recurrence-readonly`}>
                                    Este lançamento é a ocorrência <strong>{entry.recurrence_index}{entry.recurrence.occurrences ? ` de ${entry.recurrence.occurrences}` : ''}</strong> de
                                    uma série que se repete a cada <strong>{entry.recurrence.interval} {FREQUENCY_OPTIONS.find(option => option.value === entry.recurrence.frequency)?.label}</strong>{entry.recurrence.occurrences ? '' : ', sem término'}.
                                    As ocorrências são títulos independentes — alterações aqui não afetam as demais.
                                </div>
                                : <>
                                    <div className={`tab-toggle-row`}>
                                        <span>Repetir lançamento?</span>
                                        <Toggle checked={recurrenceEnabled} onChange={setRecurrenceEnabled} label={`Repetir lançamento`} disabled={isEdit} />
                                    </div>
                                    {isEdit && <p className={`tab-hint`}>A recorrência só pode ser definida na criação do lançamento.</p>}
                                    {recurrenceEnabled && !isEdit &&
                                        <>
                                            <label className={`tab-label`}>Configuração da repetição</label>
                                            <div className={`recurrence-modes`}>
                                                {RECURRENCE_MODES.map(mode => (
                                                    <button
                                                        key={mode.value}
                                                        className={recurrenceMode === mode.value ? 'active' : ''}
                                                        onClick={() => setRecurrenceMode(mode.value)}
                                                    >{mode.label}</button>
                                                ))}
                                            </div>
                                            {recurrenceMode === 'custom' &&
                                                <>
                                                    <div className="row">
                                                        <div className="col-5">
                                                            <Input label={`Repetir a cada`} value={recurrenceInterval} change={setRecurrenceInterval} type={`number`} />
                                                        </div>
                                                        <div className="col-7">
                                                            <InputSelect menuInBody label={`Frequência`} options={FREQUENCY_OPTIONS} value={recurrenceFrequency} change={setRecurrenceFrequency} />
                                                        </div>
                                                    </div>
                                                    <div className={`occurrences-row`}>
                                                        <span>Termina após</span>
                                                        <Input value={recurrenceOccurrences} change={setRecurrenceOccurrences} type={`number`} />
                                                        <span>ocorrências, incluindo esta</span>
                                                    </div>
                                                </>
                                            }
                                            {recurrencePreview &&
                                                <div className={`recurrence-preview ${recurrencePreview.error ? 'has-error' : ''}`}>
                                                    {recurrencePreview.error ?? recurrencePreview.text}
                                                </div>
                                            }
                                        </>
                                    }
                                </>
                            }
                        </div>
                    }

                    {tab === 'allocation' &&
                        <div className={`entry-tab-content`}>
                            <div className={`tab-toggle-row`}>
                                <span>Habilitar rateio?</span>
                                <Toggle checked={allocationEnabled} onChange={setAllocationEnabled} label={`Habilitar rateio`} />
                            </div>
                            {allocationEnabled &&
                                <>
                                    <div className={`allocation-dimensions`}>
                                        <button className={allocationDimension === 'category' ? 'active' : ''} onClick={() => setAllocationDimension('category')}>
                                            Categorias{categoryRows.length > 0 ? ` (${categoryRows.length})` : ''}
                                        </button>
                                        <button className={allocationDimension === 'cost_center' ? 'active' : ''} onClick={() => setAllocationDimension('cost_center')}>
                                            Centros de custo{costCenterRows.length > 0 ? ` (${costCenterRows.length})` : ''}
                                        </button>
                                    </div>
                                    {activeRows.map(row => (
                                        <div key={row.key} className={`allocation-row`}>
                                            <div className={`allocation-target`}>
                                                <InputSelect menuInBody options={activeOptions} value={row.targetId} change={value => updateAllocationRow(row.key, { targetId: value })} />
                                            </div>
                                            <div className={`allocation-amount`}>
                                                <Input value={row.amount} change={value => updateAllocationRow(row.key, { amount: value })} mask={`convertToMoney`} />
                                            </div>
                                            <div className={`allocation-percent`}>
                                                <Input value={formatPercent(convertMoenyToInt(row.amount) || 0, totalCents)} change={value => handlePercentChange(row.key, value)} right={<span>%</span>} />
                                            </div>
                                            <Button type={`delete`} svg={`delete`} action={() => removeAllocationRow(row.key)} />
                                        </div>
                                    ))}
                                    <button className={`allocation-add`} onClick={addAllocationRow}>
                                        + Adicionar {allocationDimension === 'category' ? 'categoria' : 'centro de custo'}
                                    </button>
                                    {activeRows.length > 0 &&
                                        <div className={`allocation-balance ${activeRemaining === 0 ? 'balanced' : 'unbalanced'}`}>
                                            <span>Rateado: {convertIntToMoney(rowsSum(activeRows), true)}</span>
                                            <span>Restante: {convertIntToMoney(activeRemaining, true)}</span>
                                        </div>
                                    }
                                </>
                            }
                        </div>
                    }

                    {tab === 'attachments' &&
                        <div className={`entry-tab-content`}>
                            <div className={`attachment-add-buttons`}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={event => { addFileAttachments(event.target.files); event.target.value = ''; }}
                                />
                                <button onClick={() => fileInputRef.current?.click()}>
                                    <Icon name={`upload`} />
                                    <span className={`add-label`}>
                                        <strong>Enviar arquivo</strong>
                                        <small>PDF ou imagem — até 10 MB</small>
                                    </span>
                                </button>
                                <button onClick={addLinkAttachment}>
                                    <Icon name={`link`} />
                                    <span className={`add-label`}>
                                        <strong>Adicionar link</strong>
                                        <small>URL de um documento externo</small>
                                    </span>
                                </button>
                                <button onClick={addPixAttachment}>
                                    <Icon name={`key-round`} />
                                    <span className={`add-label`}>
                                        <strong>Chave Pix</strong>
                                        <small>Para pagar ou receber</small>
                                    </span>
                                </button>
                            </div>
                            {attachments.map(attachment => (
                                <div key={attachment.key} className={`attachment-card`}>
                                    <div className={`attachment-preview`}>
                                        {attachment.previewUrl
                                            ? <img src={attachment.previewUrl} alt={attachment.name ?? ''} />
                                            : attachment.kind === 'link'
                                                ? <Icon name={`link`} />
                                                : attachment.kind === 'pix'
                                                    ? <Icon name={`key-round`} />
                                                    : <span className={`ext`}>{(getFileExtension(attachment.name) || 'doc').toUpperCase().slice(0, 4)}</span>
                                        }
                                    </div>
                                    <div className={`attachment-name`}>
                                        {renderAttachmentTarget(attachment)}
                                    </div>
                                    <div className={`attachment-desc`}>
                                        {attachment.id
                                            ? <span className={`attachment-description`} title={attachment.description}>{attachment.description || '—'}</span>
                                            : <Input placeholder={`Descrição`} value={attachment.description} change={value => updateAttachment(attachment.key, { description: value })} />
                                        }
                                    </div>
                                    <Button type={`delete`} svg={`delete`} action={() => removeAttachment(attachment)} />
                                </div>
                            ))}
                            {attachments.length === 0 &&
                                <div className={`attachments-empty`}>
                                    <Icon name={`inbox`} />
                                    <strong>Nenhum anexo por aqui</strong>
                                    <span>Contratos, notas fiscais, boletos e chaves Pix deste lançamento ficam guardados nesta aba.</span>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </Modal>
    );
};

export default ModalEntry;
